import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';

function formatEuro(cents) {
  if (cents == null) return '—';
  return `${(Number(cents) / 100).toFixed(2)} €`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
  } catch {
    return String(iso);
  }
}

function ticketIsPaid(doc) {
  const s = doc?.ticketPaymentStatus || '';
  return /VALIDE|PAYE/i.test(String(s));
}

export default function FiscalDocumentsPanel({ commercantId, onBack }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [snapshotView, setSnapshotView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState('');

  const selected = useMemo(
    () => documents.find((d) => d.id === selectedId) || null,
    [documents, selectedId]
  );

  const refresh = useCallback(async ({ preserveMessage = false } = {}) => {
    if (!commercantId) return;
    setLoading(true);
    if (!preserveMessage) {
      setMessage('');
    }
    try {
      const data = await apiService.listFiscalDocuments(commercantId, { limit: 80 });
      setDocuments(data.documents || []);
    } catch (e) {
      setMessage(e.message || 'Impossible de charger les additions');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadDetail = useCallback(
    async (doc) => {
      setSelectedId(doc.id);
      setSnapshotView(null);
      setMessage('');
      try {
        const hist = await apiService.listTicketAdditions(doc.commandeId, commercantId);
        setHistory(hist.documents || []);
      } catch (e) {
        setHistory([]);
        setMessage(e.message || 'Historique indisponible');
      }
    },
    [commercantId]
  );

  const viewOriginalSnapshot = async (doc) => {
    setBusy(true);
    setMessage('');
    try {
      const prenote =
        history.find((h) => h.documentType === 'PRENOTE' && h.status !== 'CANCELLED') ||
        history.find((h) => h.documentType === 'PRENOTE') ||
        doc;
      const res = await apiService.getTicketAddition(
        doc.commandeId,
        commercantId,
        prenote.id
      );
      setSnapshotView(res.snapshot || res.document?.snapshotJson || null);
    } catch (e) {
      setMessage(e.message || 'Document initial indisponible');
    } finally {
      setBusy(false);
    }
  };

  const runCorrection = async (doc, lineId) => {
    setBusy(true);
    setMessage('');
    try {
      const rev = Number(doc.posRevision);
      await apiService.correctTicketAddition(
        doc.commandeId,
        {
          expectedPosRevision: rev,
          reason: reason.trim() || 'Correction caisse',
          operations: [{ type: 'REMOVE_LINE', ligneId: lineId }],
          commercantId,
        },
        {
          idempotencyKey: `ui-corr-${doc.commandeId}-${rev}-${lineId}`,
          commercantId,
        }
      );
      setConfirm(null);
      setReason('');
      await refresh({ preserveMessage: true });
      if (selectedId) {
        try {
          const updated = (await apiService.listFiscalDocuments(commercantId)).documents?.find(
            (d) => d.commandeId === doc.commandeId
          );
          if (updated) await loadDetail(updated);
        } catch {
          /* détail secondaire — ne pas masquer le succès */
        }
      }
      setMessage('Correction enregistrée — l’addition d’origine est conservée.');
    } catch (e) {
      const code = e?.code || e?.data?.error?.code || e?.data?.code;
      if (code === 'TICKET_HAS_PAYMENTS') {
        setMessage(
          'Ce ticket est déjà encaissé : correction documentaire impossible ici. Utilisez remboursement ou correction prévue après paiement.'
        );
      } else {
        setMessage(e.message || 'Correction refusée');
      }
    } finally {
      setBusy(false);
    }
  };

  const runCancellation = async (doc) => {
    setBusy(true);
    setMessage('');
    try {
      const rev = Number(doc.posRevision);
      await apiService.cancelTicketAddition(
        doc.commandeId,
        {
          expectedPosRevision: rev,
          reason: reason.trim() || 'Annulation addition',
          commercantId,
        },
        {
          idempotencyKey: `ui-cancel-${doc.commandeId}-${rev}`,
          commercantId,
        }
      );
      setConfirm(null);
      setReason('');
      await refresh({ preserveMessage: true });
      setMessage('Addition annulée — le document d’origine reste archivé.');
    } catch (e) {
      const code = e?.code || e?.data?.error?.code || e?.data?.code;
      if (code === 'TICKET_HAS_PAYMENTS') {
        setMessage(
          'Ticket déjà encaissé : l’annulation du document seule n’est pas autorisée. Aucun remboursement automatique.'
        );
      } else {
        setMessage(e.message || 'Annulation refusée');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pos-caisse-work-surface" data-testid="caisse-fiscal-docs-panel">
      <header className="pos-caisse-work-surface-header flex flex-wrap items-center justify-between gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="pos-touch-target flex items-center gap-2 text-slate-600 hover:text-slate-900 min-h-[44px] px-2 rounded-xl hover:bg-slate-100"
        >
          <ArrowLeft className="h-5 w-5" /> Retour
        </button>
        <h2 className="text-xl font-bold text-slate-900 flex-1 min-w-[8rem]">Additions fiscales</h2>
        <button type="button" onClick={refresh} className="pos-touch-target p-2 rounded-xl hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
          <RefreshCw className="h-5 w-5 text-violet-600" />
        </button>
      </header>

      <p className="text-sm text-slate-600 mb-4">
        Consultation des documents émis. Corriger ou annuler ne remplace jamais silencieusement
        l’original. Ce n’est pas une attestation de conformité.
      </p>

      {message ? (
        <p
          data-testid="caisse-fiscal-feedback"
          className="text-sm text-slate-800 bg-slate-50 rounded-xl px-3 py-2 mb-4"
        >
          {message}
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          {loading ? (
            <p className="text-slate-500">Chargement…</p>
          ) : documents.length === 0 ? (
            <p className="text-slate-500">Aucune addition enregistrée</p>
          ) : (
            <ul className="space-y-2 max-h-[28rem] overflow-y-auto">
              {documents.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    data-testid="caisse-fiscal-doc-row"
                    data-document-id={d.id}
                    onClick={() => loadDetail(d)}
                    className={`w-full text-left rounded-xl border p-3 ${
                      selectedId === d.id ? 'border-violet-400 bg-violet-50' : 'border-slate-100 bg-white'
                    }`}
                  >
                    <p className="font-semibold text-slate-900">{d.numero}</p>
                    <p className="text-xs text-slate-500">
                      {d.documentType} · {d.status} · {formatDate(d.issuedAt)}
                    </p>
                    <p className="text-sm text-slate-700">
                      Ticket {d.commandeId?.slice(0, 12)}… · {formatEuro(d.totalCents)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className="bg-white rounded-2xl border border-slate-100 p-4 min-h-[12rem]"
          data-testid="caisse-fiscal-doc-detail"
        >
          {!selected ? (
            <p className="text-slate-500 text-sm">Sélectionnez une addition pour voir le détail.</p>
          ) : (
            <>
              <p className="font-bold text-slate-900">{selected.numero}</p>
              <p className="text-sm text-slate-600 mt-1">
                État : {selected.status} · TVA {formatEuro(selected.tvaCents)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Ticket {selected.commandeId} · révision POS {selected.posRevision}
              </p>
              {selected.reason ? (
                <p className="text-sm mt-2">Motif : {selected.reason}</p>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="caisse-fiscal-voir-document"
                  disabled={busy}
                  className="rounded-lg bg-slate-800 text-white px-3 py-1.5 text-sm"
                  onClick={() => viewOriginalSnapshot(selected)}
                >
                  Voir document initial
                </button>
                {!ticketIsPaid(selected) &&
                selected.status !== 'CANCELLED' &&
                selected.ticketFiscalStatus !== 'CANCELLED' ? (
                  <>
                    <button
                      type="button"
                      data-testid="caisse-fiscal-corriger-addition"
                      disabled={busy}
                      className="rounded-lg border border-amber-500 text-amber-800 px-3 py-1.5 text-sm"
                      onClick={() =>
                        setConfirm({ type: 'correct', doc: selected })
                      }
                    >
                      Corriger
                    </button>
                    <button
                      type="button"
                      data-testid="caisse-fiscal-annuler-addition"
                      disabled={busy}
                      className="rounded-lg border border-red-400 text-red-700 px-3 py-1.5 text-sm"
                      onClick={() => setConfirm({ type: 'cancel', doc: selected })}
                    >
                      Annuler l’addition
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-slate-500 w-full" data-testid="caisse-fiscal-actions-blocked">
                    {ticketIsPaid(selected)
                      ? 'Ticket encaissé — correction/annulation documentaire indisponible.'
                      : 'Aucune action documentaire disponible pour cet état.'}
                  </p>
                )}
              </div>

              {snapshotView ? (
                <div className="mt-4 text-sm bg-slate-50 rounded-xl p-3 max-h-48 overflow-auto">
                  <p className="font-medium mb-1">Snapshot d’origine (lecture seule)</p>
                  <p>Total : {formatEuro(snapshotView.totalCents)}</p>
                  {Array.isArray(snapshotView.lines) ? (
                    <ul className="mt-2 list-disc pl-4">
                      {snapshotView.lines.map((ln, i) => (
                        <li key={i}>
                          {ln.designation || ln.nom || 'Ligne'} ·{' '}
                          {formatEuro(ln.totalCents ?? ln.amountCents)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {history.length > 0 ? (
                <div className="mt-4">
                  <p className="text-sm font-semibold">Historique des documents</p>
                  <ul className="mt-1 space-y-1 text-xs text-slate-600">
                    {history.map((h) => (
                      <li key={h.id} data-testid="caisse-fiscal-history-row">
                        {h.numero} — {h.documentType} — {h.status}
                        {h.parentDocumentId ? ` (suite de ${h.parentDocumentId.slice(0, 8)}…)` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {confirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-5 max-w-md w-full shadow-xl">
            <h3 className="font-bold text-lg">
              {confirm.type === 'cancel' ? 'Annuler l’addition ?' : 'Corriger l’addition ?'}
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              {confirm.type === 'cancel'
                ? 'Le document d’origine reste conservé. Aucun remboursement bancaire automatique.'
                : 'Une correction crée un nouveau document. L’original n’est pas modifié.'}
            </p>
            <label className="block mt-3 text-sm">
              Motif (recommandé)
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            {confirm.type === 'correct' ? (
              <p className="text-xs text-slate-500 mt-2">
                La démonstration retire la première ligne du ticket (correction type « ligne en moins »).
              </p>
            ) : null}
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border"
                onClick={() => {
                  setConfirm(null);
                  setReason('');
                }}
              >
                Retour
              </button>
              <button
                type="button"
                data-testid="caisse-fiscal-confirm-action"
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white disabled:opacity-50"
                onClick={async () => {
                  if (confirm.type === 'cancel') {
                    await runCancellation(confirm.doc);
                    return;
                  }
                  const detail = await apiService.getTicketDetail(
                    confirm.doc.commandeId,
                    commercantId
                  );
                  const ticket = detail.ticket || detail;
                  const line = ticket.produits?.[0];
                  if (!line?.id) {
                    setMessage('Aucune ligne à corriger sur ce ticket');
                    setConfirm(null);
                    return;
                  }
                  await runCorrection(confirm.doc, line.id);
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
